import { CalendarToday, ChangeCircleOutlined, ChevronRight as IconChevronRight, People } from "@mui/icons-material";
import { darken, Stack, Typography } from "@mui/material";
import { PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, To } from "react-router";

import { GqlPlannerV2VisitPossibleDaysStatus } from "~/__generated__";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDateTime } from "~/modules/app/util/formatters";

import { IconVisit } from "../IconVisit";
import { AreaCardVisitItemFragment } from "./fragments.generated";

type AreaCardVisitItemProps = {
    visit: AreaCardVisitItemFragment;
    showFieldworkers?: boolean;
    isToBeCancelled?: boolean;
    icon?: ReactNode;
    disabled?: boolean;
} & (
    | { href: To; onClick?: never } // .
    | { onClick: () => void; href?: never }
);

export function AreaCardVisitItem({
    href,
    onClick,
    visit,
    showFieldworkers = false,
    isToBeCancelled = false,
    icon,
    disabled = false,
    children,
}: PropsWithChildren<AreaCardVisitItemProps>) {
    const { t } = useTranslation("planner");

    const showVisitStartTime = visit.plannerStatus === GqlPlannerV2VisitPossibleDaysStatus.Planned && visit.startTime !== null;

    return (
        <Stack
            paddingInline={2}
            paddingBlock={1}
            rowGap={1}
            sx={theme => ({
                backgroundColor: theme.palette.background.default,
                borderTop: `1px solid ${theme.palette.divider}`,
                opacity: disabled ? 0.5 : 1,
                "&:hover": {
                    backgroundColor: disabled ? theme.palette.background.default : darken(theme.palette.background.default, 0.03),
                },
            })}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                gap={2}
                alignItems="center"
                {...(href && {
                    component: Link,
                    to: href,
                })}
                onClick={onClick}
                sx={theme => ({
                    color: theme.palette.text.primary,
                    textDecoration: "none",
                    cursor: "pointer",
                })}
            >
                <Stack alignSelf="start" alignItems="center" rowGap={1}>
                    <IconVisit status={visit.plannerStatus} size="small" />
                    {visit.hasStagedChanges && <ChangeCircleOutlined color="warning" fontSize="small" />}
                </Stack>
                <Stack flexGrow={1} gap={0.5}>
                    <Typography flexGrow={1} variant="body1" sx={{ textDecoration: isToBeCancelled ? "line-through" : "none" }}>
                        {visit.protocol.name}
                    </Typography>
                    <Typography flexGrow={1} variant="subtitle2" color="textSecondary">
                        {visit.protocolVisit.name ?? t("visitInfo.visitNumber", { visitNumber: visit.protocolVisit.number })}
                    </Typography>

                    {(showFieldworkers || showVisitStartTime) && (
                        <Stack>
                            {showVisitStartTime && (
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                    sx={{ display: "flex", alignItems: "center", columnGap: 1 }}
                                >
                                    <CalendarToday fontSize="inherit" />{" "}
                                    {formatDateTime(parseStringToDate(visit.startTime as string), "long")}
                                </Typography>
                            )}
                            {showFieldworkers && (
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                    sx={{ display: "flex", alignItems: "center", columnGap: 1 }}
                                >
                                    <People fontSize="inherit" />
                                    This visit has {visit.plannedFieldworkersStaged}/{visit.requiredFieldworkers} fieldworkers
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Stack>
                {icon ? icon : <IconChevronRight sx={{ alignSelf: "center" }} />}
            </Stack>
            {children}
        </Stack>
    );
}
